<template>
  <BaseModal
    :modalTitle="$t('W: Importer brugere')"
    :modalIntro="welcomeEmailIntroText">
    <!-- Step 1 — pick a file -->
    <div
      v-if="step === 'select'"
      class="flex flex-col gap-4 px-6 pb-6">
      <InfoText
        v-if="!isWelcomeEmailEnabled"
        theme="warning"
        :text="noWelcomeEmailWarningText" />

      <!-- Everything for picking a file goes away once one is loaded — "Nulstil" brings it back. -->
      <div
        v-if="!parsed"
        class="flex flex-col gap-4">
        <p class="text-text-subtle text-sm">
          {{
            $t(
              'W: Hent skabelonen, udfyld én bruger pr. række, og upload filen igen.'
            )
          }}
        </p>

        <div>
          <button
            type="button"
            class="flex cursor-pointer items-center gap-2 text-sm text-action hover:underline"
            @click="onDownloadTemplateClick">
            <IconLucideDownload class="h-4 w-4 shrink-0" />
            {{ $t('W: Hent skabelon (CSV)') }}
          </button>
          <p class="text-text-subtle mt-1 text-xs">
            {{
              $t('W: Understøttede lande: {countries}', {
                countries: supportedCountries,
              })
            }}
          </p>
        </div>

        <UiFileUpload
          :key="uploadKey"
          :modelValue="[]"
          :allowedFileTypes="['csv']"
          :maxFiles="1"
          @on-file-selected="onFileSelected" />

        <!-- DEV only: fills in a file that hits every row-level rule, no upload needed -->
        <div
          v-if="isDevToolsVisible"
          class="flex items-center gap-2">
          <DevBadge />
          <button
            type="button"
            class="cursor-pointer text-sm text-action hover:underline"
            @click="onInsertTestCsvClick">
            {{ $t('W: Indsæt test-CSV med alle edge cases') }}
          </button>
        </div>
      </div>

      <p
        v-if="fileErrorText"
        class="text-text-error text-sm"
        role="alert">
        {{ fileErrorText }}
      </p>

      <!-- What the file will do, row by row, before anything is sent -->
      <template v-if="parsed">
        <div class="flex flex-wrap items-center gap-2">
          <UiStatusChip
            variant="green"
            :label="
              $t('W: {count} brugere klar til import', {
                count: parsed.rows.length,
              })
            " />
          <UiStatusChip
            v-if="rejectedBeforeSend.length"
            variant="red"
            :label="
              $t('W: {count} rækker springes over', {
                count: rejectedBeforeSend.length,
              })
            " />
        </div>

        <!-- Filter by outcome. The table below is already sorted problems first. -->
        <div class="flex flex-wrap items-center justify-between gap-3">
          <UiRadioTabs
            :options="statusFilterOptions"
            :modelValue="statusFilter"
            @update:model-value="onStatusFilterChange" />
          <div class="flex items-center gap-2">
            <AppSwitch
              :isActive="showAllData"
              :title="$t('W: Vis alle data')"
              @on-toggle="showAllData = !showAllData" />
            <span class="text-sm text-text-primary">
              {{ $t('W: Vis alle data') }}
            </span>
          </div>
        </div>

        <!-- The table's own empty controls bar would leave a blank strip above the header here. -->
        <!-- One scroll area for both directions: the table's own horizontal scroller would sit
        below the last row, out of sight once the list is taller than this box. -->
        <div
          class="max-h-96 overflow-auto [&_.table-controls]:hidden [&_.table-scroll-wrap]:overflow-visible">
          <UiDynamicTable
            :headerRow="previewHeaderRow"
            :bodyRows="previewBodyRows" />
        </div>
      </template>

      <div class="flex justify-end gap-2">
        <UiBaseButton
          v-if="parsed || fileErrorText"
          :text="$t('W: Nulstil')"
          theme="neutral"
          :isDisabled="isLoading"
          @click="onResetClick" />
        <UiBaseButton
          :text="$t('W: Importer')"
          :isDisabled="!canSubmit"
          :isLoading="isLoading"
          @click="onSubmit" />
      </div>
    </div>

    <!-- Step 2 — result -->
    <div
      v-else
      class="flex flex-col gap-4 px-6 pb-6">
      <p class="text-base font-medium">{{ resultHeadline }}</p>

      <div
        v-if="resultExceptions.length"
        class="flex flex-col gap-1">
        <p class="text-sm font-medium">
          {{ $t('W: Følgende rækker blev ikke oprettet') }}
        </p>
        <ul class="flex flex-col gap-1">
          <li
            v-for="row in resultExceptions"
            :key="row.rowNumber"
            class="text-text-subtle text-xs">
            {{ $t('W: Række {row}', { row: row.rowNumber })
            }}{{ row.email ? ` (${row.email})` : '' }}: {{ row.reasonText }}
          </li>
        </ul>
      </div>

      <div class="flex justify-end">
        <UiBaseButton
          :text="$t('W: Luk')"
          @click="closeTop" />
      </div>
    </div>
  </BaseModal>
</template>

<script lang="ts">
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { useToast } from '@/globals/typescript/utils/toast'
import { downloadBlob } from '@/globals/typescript/utils/downloadBlob'
import { reportUnexpectedError } from '@/globals/typescript/utils/reportError'
import {
  buildCustomerUserImportTemplate,
  parseCustomerUserImportCsv,
  excludeExistingEmails,
  CUSTOMER_USER_IMPORT_MAX_ROWS,
  SUPPORTED_IMPORT_COUNTRIES,
  type TCustomerUserImportParseResult,
} from '@/globals/typescript/utils/customerUserImport'
import { mapActions, mapState } from 'pinia'
import { useUserStore } from '@/stores/userStore'
import { useAppStore } from '@/stores/appStore'
import {
  useCustomerUserStore,
  type TCompanyCustomerUser,
} from '@/stores/customerUserStore'
import { isWelcomeEmailEnabledForCustomer } from '@/globals/typescript/utils/customerOwner'
import { getEmailInUseByAnotherUserMessage } from '@/globals/typescript/utils/errorMessages'
import type { ICustomerRef } from '@team-milva/lab-models/customer'
import {
  getNoWelcomeEmailWarningText,
  getWelcomeEmailIntroText,
} from '@/globals/typescript/utils/welcomeEmailNotice'
import { buildImportEdgeCaseCsv } from '@/dev/importTestCsv'
import { formatPhoneNumber } from '@team-milva/utils/phone'
import type { SupportedCountries } from '@team-milva/utils/phone'
import type { PropType } from 'vue'
import type { IBodyRow, IHeaderRow } from '@team-milva/lab-components'

type TDisplayRow = { rowNumber: number; email: string; reasonText: string }

type TStatusFilter = 'all' | 'ready' | 'skipped'

type TPreviewRow = {
  rowNumber: number
  firstName: string
  lastName: string
  email: string
  country: string
  phone: string
  outcome: 'ready' | 'skipped'
  reasonText: string
}

type TParsedOk = Extract<TCustomerUserImportParseResult, { ok: true }>

export default {
  name: 'ImportCustomerUsersBusinessModal',

  // A business or an insurance customer (MIL-402) — the API path follows `customer`.
  props: {
    customer: { type: Object as PropType<ICustomerRef>, required: true },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      step: 'select' as 'select' | 'result',
      parsed: null as TParsedOk | null,
      statusFilter: 'all' as TStatusFilter,
      showAllData: false,
      uploadKey: 0,
      fileErrorText: '',
      isLoading: false,
      totalCount: 0,
      createdCount: 0,
      resultExceptions: [] as TDisplayRow[],
    }
  },

  computed: {
    ...mapState(useUserStore, ['isDeveloper', 'showDevActions']),
    ...mapState(useAppStore, ['currentLocale']),
    ...mapState(useCustomerUserStore, [
      'customerUsersBusiness',
      'customerUsersInsurance',
    ]),

    isWelcomeEmailEnabled(): boolean {
      return isWelcomeEmailEnabledForCustomer(this.customer)
    },

    /**
     * Every business and insurance user the lab can see. An email is one account across both
     * customer types (MIL-402), so a row whose email any of them already has is skipped up front.
     */
    companyCustomerUsers(): TCompanyCustomerUser[] {
      return [...this.customerUsersBusiness, ...this.customerUsersInsurance]
    },

    welcomeEmailIntroText(): string {
      return this.isWelcomeEmailEnabled
        ? getWelcomeEmailIntroText({ plural: true })
        : ''
    },

    noWelcomeEmailWarningText(): string {
      return getNoWelcomeEmailWarningText()
    },

    /** A user already on this account, so the dev file's "findes allerede" row really is one. */
    existingUserEmail(): string | undefined {
      return this.companyCustomerUsers.find(
        (user) =>
          user.customer.type === this.customer.type &&
          user.customer.id === this.customer.id
      )?.email
    },

    isDevToolsVisible(): boolean {
      return this.isDeveloper && this.showDevActions
    },

    supportedCountries(): string {
      return SUPPORTED_IMPORT_COUNTRIES.map((c) => c.toUpperCase()).join(', ')
    },

    canSubmit(): boolean {
      return !this.isLoading && !!this.parsed && this.parsed.rows.length > 0
    },

    /**
     * Rows the parser disqualified locally. Shown before submit so the user can
     * fix the file rather than discovering the gaps in the result screen.
     */
    rejectedBeforeSend(): TDisplayRow[] {
      if (!this.parsed) return []

      return [...this.parsed.invalidRows, ...this.parsed.duplicateRows]
        .map((row) => ({
          rowNumber: row.rowNumber,
          email: row.email,
          reasonText: this.reasonText(row.reason),
        }))
        .sort((a, b) => a.rowNumber - b.rowNumber)
    },

    /** Every row of the file in file order — the ones that will be created and the ones that will not. */
    previewRows(): TPreviewRow[] {
      if (!this.parsed) return []

      const ready: TPreviewRow[] = this.parsed.rows.map((row) => ({
        rowNumber: row.rowNumber,
        firstName: row.firstName,
        lastName: row.lastName,
        email: row.email,
        country: row.phone.iso2.toUpperCase(),
        phone: this.formatPhone(row.phone.iso2, row.phone.number),
        outcome: 'ready',
        reasonText: '',
      }))
      const skipped: TPreviewRow[] = this.parsed.invalidRows.map((row) => ({
        ...row,
        outcome: 'skipped',
        reasonText: this.reasonText(row.reason),
      }))
      // A duplicate in the file is just another row that is skipped — the reason column says why.
      const duplicates: TPreviewRow[] = this.parsed.duplicateRows.map(
        (row) => ({
          ...row,
          outcome: 'skipped',
          reasonText: this.reasonText(row.reason),
        })
      )

      // Sorted by outcome, problems first — what the user has to fix is what they came to see —
      // then by reason, so rows with the same problem sit together, then by row number so each
      // run still reads in file order.
      const outcomeRank = { skipped: 0, ready: 1 } as const

      return [...ready, ...skipped, ...duplicates].sort(
        (a, b) =>
          outcomeRank[a.outcome] - outcomeRank[b.outcome] ||
          a.reasonText.localeCompare(b.reasonText, this.currentLocale) ||
          a.rowNumber - b.rowNumber
      )
    },

    visiblePreviewRows(): TPreviewRow[] {
      if (this.statusFilter === 'all') return this.previewRows
      return this.previewRows.filter((row) => row.outcome === this.statusFilter)
    },

    statusFilterOptions(): { value: TStatusFilter; text: string }[] {
      const count = (outcome: TPreviewRow['outcome']): number =>
        this.previewRows.filter((row) => row.outcome === outcome).length

      return [
        {
          value: 'all',
          text: this.$t('W: Alle ({count})', {
            count: this.previewRows.length,
          }),
        },
        {
          value: 'skipped',
          text: this.$t('W: Springes over ({count})', {
            count: count('skipped'),
          }),
        },
        {
          value: 'ready',
          text: this.$t('W: Oprettes ({count})', { count: count('ready') }),
        },
      ]
    },

    /**
     * Compact by default: row number, e-mail, outcome and reason — the e-mail is what identifies a
     * user. "Vis alle data" adds the name and the phone number, with the country under it.
     *
     * Few columns, each with a real width, on purpose: a "fill" column only gets what is left over
     * and was squeezed to a few pixels, cutting the reason off. With fixed widths the table is
     * simply as wide as its columns and scrolls sideways when the window is narrower.
     */
    previewHeaderRow(): IHeaderRow {
      const outcome = [
        { title: this.$t('W: Udfald'), size: 'sm' as const },
        { title: this.$t('W: Årsag'), size: 'xl' as const },
      ]

      if (!this.showAllData) {
        return {
          items: [
            { title: this.$t('W: Række'), size: 'xs' },
            { title: this.$t('W: E-mail'), size: 'xl' },
            ...outcome,
          ],
        }
      }

      return {
        items: [
          { title: this.$t('W: Række'), size: 'xs' },
          { title: this.$t('term.name'), size: 'md' },
          { title: this.$t('W: E-mail'), size: 'xl' },
          { title: this.$t('W: Telefon'), size: 'md' },
          ...outcome,
        ],
      }
    },

    previewBodyRows(): IBodyRow[] {
      const text = (value: string) => ({ first: { value: value || '–' } })

      return this.visiblePreviewRows.map((row) => {
        const outcomeCells = [
          {
            chip: {
              variant:
                row.outcome === 'ready' ? ('green' as const) : ('red' as const),
              label:
                row.outcome === 'ready'
                  ? this.$t('W: Oprettes')
                  : this.$t('W: Springes over'),
            },
            first: { value: '' },
          },
          text(row.reasonText),
        ]

        return {
          id: String(row.rowNumber),
          items: this.showAllData
            ? [
                text(String(row.rowNumber)),
                text([row.firstName, row.lastName].filter(Boolean).join(' ')),
                text(row.email),
                {
                  first: { value: row.phone || '–' },
                  ...(row.country
                    ? { second: { value: row.country.toUpperCase() } }
                    : {}),
                },
                ...outcomeCells,
              ]
            : [text(String(row.rowNumber)), text(row.email), ...outcomeCells],
        }
      })
    },

    resultHeadline(): string {
      if (this.createdCount === 0) {
        return this.$t('W: Ingen brugere blev oprettet')
      }

      if (this.createdCount === this.totalCount) {
        return this.createdCount === 1
          ? this.$t('W: 1 bruger blev importeret')
          : this.$t('W: Alle {count} brugere blev importeret', {
              count: this.createdCount,
            })
      }

      return this.$t('W: {created} af {total} brugere blev importeret', {
        created: this.createdCount,
        total: this.totalCount,
      })
    },
  },

  methods: {
    ...mapActions(useCustomerUserStore, ['importCustomerUsers']),
    ...mapActions(useModalStore, ['closeTop']),

    /** Maps a machine reason code to something a human can act on. */
    reasonText(reason: string): string {
      const map: Record<string, string> = {
        'first-name-missing': this.$t('W: Fornavn mangler'),
        'last-name-missing': this.$t('W: Efternavn mangler'),
        'email-missing': this.$t('W: E-mail mangler'),
        'email-invalid': this.$t('W: E-mail er ikke gyldig'),
        'country-missing': this.$t('W: Land mangler'),
        'country-unsupported': this.$t('W: Landet understøttes ikke'),
        'phone-missing': this.$t('W: Telefonnummer mangler'),
        'phone-invalid': this.$t('W: Telefonnummer er ikke gyldigt'),
        'duplicate-in-file': this.$t('W: E-mailen står flere gange i filen'),
        'already-exists': getEmailInUseByAnotherUserMessage(),
        'unexpected-error': this.$t(
          'W: Der opstod en uventet fejl — tjek om brugeren blev oprettet'
        ),
      }

      return map[reason] ?? this.$t('W: Rækken kunne ikke bruges')
    },

    // DEV ONLY — goes through the same path as a picked file, so the preview and the rules it shows
    // are exactly what a real upload would get.
    async onInsertTestCsvClick() {
      const file = new File(
        [buildImportEdgeCaseCsv(undefined, this.existingUserEmail)],
        'test-edge-cases.csv',
        {
          type: 'text/csv',
        }
      )
      await this.onFileSelected(file)
    },

    /** Throws the loaded file away and brings the uploader back, instead of uploading over it. */
    onResetClick(): void {
      this.parsed = null
      this.fileErrorText = ''
      this.statusFilter = 'all'
      this.uploadKey += 1
      this.unsavedGuard.takeSnapshot()
    },

    /** "+45 12 34 56 78" — the country's own grouping and dial code, from the shared phone module. */
    formatPhone(iso2: string, number: number): string {
      return formatPhoneNumber(String(number), iso2 as SupportedCountries, true)
    },

    onStatusFilterChange(value: string): void {
      this.statusFilter = value as TStatusFilter
    },

    fileErrorFor(error: string): string {
      if (error === 'empty-file') return this.$t('W: Filen er tom')
      if (error === 'no-data-rows') {
        return this.$t('W: Filen indeholder ingen rækker med brugere')
      }
      if (error.startsWith('too-many-rows')) {
        return this.$t('W: Filen indeholder flere end {max} rækker', {
          max: CUSTOMER_USER_IMPORT_MAX_ROWS,
        })
      }
      if (error.startsWith('missing-columns:')) {
        return this.$t('W: Filen mangler kolonnerne: {columns}', {
          columns: error.replace('missing-columns:', ''),
        })
      }

      return this.$t('W: Filen kunne ikke læses')
    },

    onDownloadTemplateClick() {
      const blob = new Blob([buildCustomerUserImportTemplate()], {
        type: 'text/csv;charset=utf-8',
      })
      downloadBlob(blob, 'brugere-import-skabelon.csv')
    },

    async onFileSelected(file: File) {
      this.fileErrorText = ''
      this.parsed = null
      this.statusFilter = 'all'

      try {
        const text = await file.text()
        const result = parseCustomerUserImportCsv(text)

        if (!result.ok) {
          this.fileErrorText = this.fileErrorFor(result.error)
          return
        }

        // Users the lab can already see are skipped here, so an existing user can never be
        // submitted — the server's own `already-exists` stays as the backstop for the rest.
        this.parsed = excludeExistingEmails(
          result,
          this.companyCustomerUsers.map((user) => user.email)
        )
        const importableRowCount = this.parsed.rows.length

        if (importableRowCount === 0) {
          this.fileErrorText = this.$t(
            'W: Ingen af rækkerne i filen kunne bruges'
          )
        }
      } catch (error) {
        reportUnexpectedError(error, { operation: 'customer-user-import-read' })
        this.fileErrorText = this.$t('W: Filen kunne ikke læses')
      }
    },

    async onSubmit() {
      if (!this.parsed || this.isLoading) return

      this.isLoading = true

      try {
        const result = await this.importCustomerUsers(
          this.customer,
          this.parsed.rows
        )

        // Rows the server rejected, plus the ones never sent — the user should
        // see one list covering everything that did not become a user, not two.
        const serverExceptions: TDisplayRow[] = result.outcomes
          .filter((outcome) => outcome.status !== 'created')
          .map((outcome) => ({
            rowNumber: outcome.rowNumber,
            email: outcome.email,
            reasonText: this.reasonText(
              outcome.status === 'already-exists'
                ? 'already-exists'
                : (outcome.reason ?? '')
            ),
          }))

        this.resultExceptions = [
          ...this.rejectedBeforeSend,
          ...serverExceptions,
        ].sort((a, b) => a.rowNumber - b.rowNumber)

        this.createdCount = result.createdCount
        this.totalCount = result.totalCount + this.rejectedBeforeSend.length
        this.step = 'result'
        // The result screen holds nothing to lose — without this, Escape or X would warn about
        // unsaved changes straight after a successful import.
        this.unsavedGuard.takeSnapshot()

        const welcomeEmailFailedCount = result.outcomes.filter(
          (outcome) => outcome.welcomeEmailFailed
        ).length

        if (welcomeEmailFailedCount > 0) {
          // Created, but not mailed — said out loud, since there is no list of who was mailed.
          useToast().error(
            this.$t(
              'W: {count} brugere blev oprettet, men velkomstmailen kunne ikke sendes til {failed}',
              { count: result.createdCount, failed: welcomeEmailFailedCount }
            )
          )
        } else if (result.createdCount > 0) {
          useToast().success(
            this.isWelcomeEmailEnabled
              ? this.$t(
                  'W: {count} brugere blev oprettet – velkomstmail sendt til alle',
                  {
                    count: result.createdCount,
                  }
                )
              : this.$t(
                  'W: {count} brugere blev oprettet – ingen velkomstmails sendt',
                  {
                    count: result.createdCount,
                  }
                )
          )
        }

        // Emails another user already has — one the lab cannot see here (lab staff, a user of
        // another lab) — only show up now. Said out loud, not just listed on the result screen.
        const emailInUseCount = result.outcomes.filter(
          (outcome) => outcome.status === 'already-exists'
        ).length

        if (emailInUseCount > 0) {
          useToast().error(
            emailInUseCount === 1
              ? getEmailInUseByAnotherUserMessage()
              : this.$t(
                  'W: {count} e-mails er allerede i brug af andre brugere',
                  { count: emailInUseCount }
                )
          )
        }
      } catch (error) {
        reportUnexpectedError(error, { operation: 'customer-user-import' })
        useToast().error(this.$t('W: Importen kunne ikke gennemføres'))
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    // A chosen and parsed file is real work — Escape or the X would otherwise discard it
    // silently, unlike every other draft-holding create modal. `modalId` is what lets ModalHost
    // tell "the modal being closed is dirty" apart from "a layer underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({
        step: this.step,
        parsedRowCount: this.parsed?.rows.length ?? 0,
      }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
